<template>
  <modal
    :loading="loading"
    :title="$t('myassets.run_model_filter_title')"
    @close_modal="close_modal"
  >
    <template v-slot:buttons>
      <primary-button
        :label="primaryRunLabel"
        :disabled="!selected_model || !can_primary_run"
        @pressed="run_primary"
      />
    </template>
    <template v-slot:content>
      <my-assets-run-target-banner
        :use-selection="useSelection"
        :selection-count="selectionCount"
        @clear-selection="clearSelectionAndUseFilter"
      />

      <p v-if="!runTargetIsSelection && !can_run_filter" class="text-caption text-grey-darken-1 mb-2">
        {{ $t("myassets.need_filter_for_batch") }}
      </p>
      <autocomplete
        v-if="filtered_models.length > 0"
        :field_label="$t('annotate.placeholder')"
        :prepend_icon="'far fa-circle-play'"
        :options="filtered_models"
        :name="modelDisplayLabel"
        @select="on_model_select"
        @cleared="on_model_cleared"
        @updated_search_value="model_search = $event"
      />
      <p v-else-if="models_loaded" class="text-caption">
        {{ $t("myassets.no_active_models") }}
      </p>
      <p v-if="!runTargetIsSelection" class="text-caption mt-2 mb-0">
        {{ $t("myassets.run_model_on_filter_hint") }}
      </p>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import PrimaryButton from "@/components/form/PrimaryButton";
import Autocomplete from "@/components/Autocomplete.vue";
import MyAssetsRunTargetBanner from "@/components/myassets/MyAssetsRunTargetBanner.vue";
import { flatFilters } from "@/components/asset_filter/filterQuery";

export default {
  name: "RunModelFilterModal",
  components: { Autocomplete, PrimaryButton, Modal, MyAssetsRunTargetBanner },
  props: {
    pageFilters: { type: [Array, Object, Boolean], default: false },
    selectedAssetIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      selected_model: null,
      models_loaded: false,
      useSelection: false,
      userClearedSelection: false,
      model_search: '',
    };
  },
  computed: {
    selectionAssetIds() {
      return (this.selectedAssetIds || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));
    },
    models() {
      return this.$store.getters["model/get_items"] || [];
    },
    filtered_models() {
      const search = this.model_search.toLowerCase();
      return this.models.filter(model => {
        const matchesSearch = !search || model.name.toLowerCase().includes(search);
        return matchesSearch;
      });
    },
    active_models() {
      return this.filtered_models.filter((m) => m.active && !m.archived);
    },
    modelDisplayLabel() {
      return (item) => {
        let label = item.name;
        
        // Build model type and size info
        const parts = [];
        
        // Add model type if available
        if (item.type) {
          parts.push(item.type);
        }
        
        // Add size from meta if available
        if (item.meta) {
          try {
            const meta = JSON.parse(item.meta);
            // Check for image_size or size in meta
            if (meta.image_size) {
              parts.push(meta.image_size);
            } else if (meta.size) {
              parts.push(meta.size);
            }
            // Check for model_type in meta (different from item.type)
            if (meta.model_type && !parts.includes(meta.model_type)) {
              parts.push(meta.model_type);
            }
          } catch (e) {
            // Invalid JSON, ignore
          }
        }
        
        // Add (active) indicator if needed
        if (item.active && !item.archived) {
          parts.push('active');
        }
        
        if (parts.length > 0) {
          label = `${label} (${parts.join(' ')})`;
        }
        
        return label;
      };
    },
    selectionCount() {
      return this.selectionAssetIds.length;
    },
    can_run_filter() {
      return flatFilters(this.pageFilters || []).length > 0;
    },
    can_run_selection() {
      return this.selectionCount > 0;
    },
    runTargetIsSelection() {
      return this.useSelection && this.can_run_selection;
    },
    primaryRunLabel() {
      return this.runTargetIsSelection
        ? this.$t('myassets.run_model_on_selection')
        : this.$t('myassets.run_model_on_filter');
    },
    can_primary_run() {
      if (this.runTargetIsSelection) {
        return this.can_run_selection;
      }
      return this.can_run_filter;
    },
  },
  async mounted() {
    this.userClearedSelection = false;
    this.useSelection = this.can_run_selection;
    await this.$store.dispatch("model/all");
    this.models_loaded = true;
  },
  watch: {
    selectedAssetIds: {
      handler() {
        if (this.userClearedSelection) {
          return;
        }
        if (this.can_run_selection) {
          this.useSelection = true;
        } else if (this.useSelection) {
          this.useSelection = false;
        }
      },
    },
  },
  methods: {
    close_modal() {
      this.$emit("close_modal");
    },
    clearSelectionAndUseFilter() {
      this.userClearedSelection = true;
      this.useSelection = false;
      this.$emit("clear-selection");
    },
    on_model_select(model) {
      this.selected_model = model && model.id != null ? model : null;
    },
    on_model_cleared() {
      this.selected_model = null;
    },
    run_primary() {
      if (this.runTargetIsSelection) {
        this.run_on_selection();
        return;
      }
      this.run_on_filter();
    },
    async run_on_filter() {
      if (!this.selected_model || !this.can_run_filter) {
        return;
      }
      this.loading = true;
      try {
        await this.$api.asset.analyse_with_filter({
          model: this.selected_model,
          filter: this.pageFilters,
        });
        this.$store.dispatch("snackbars/add_success_snack", this.$t("myassets.run_model_queued"));
        this.close_modal();
      } catch (e) {
        this.$store.dispatch("snackbars/add_failed_snack", this.$t("myassets.run_model_failed"));
      } finally {
        this.loading = false;
      }
    },
    async run_on_selection() {
      if (!this.selected_model || !this.runTargetIsSelection) {
        return;
      }
      this.loading = true;
      try {
        await this.$api.asset.analyse_selected({
          model: this.selected_model,
          asset_ids: this.selectionAssetIds,
        });
        this.$store.dispatch("snackbars/add_success_snack", this.$t("myassets.run_model_queued"));
        this.close_modal();
      } catch (e) {
        this.$store.dispatch("snackbars/add_failed_snack", this.$t("myassets.run_model_failed"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
